let activeAudio: HTMLAudioElement | null = null;
let settleActivePlayback: (() => void) | null = null;
let captureOwner: symbol | null = null;
let playbackGeneration = 0;
const AUDIO_START_TIMEOUT_MS = 15_000;

export function beginWebAudioCapture(): (() => void) | null {
  if (captureOwner) return null;
  const owner = Symbol("speech-capture");
  captureOwner = owner;
  playbackGeneration += 1;
  cancelActivePlayback();
  return () => {
    if (captureOwner === owner) captureOwner = null;
  };
}

function cancelActivePlayback(): void {
  const audio = activeAudio;
  const settle = settleActivePlayback;
  activeAudio = null;
  settleActivePlayback = null;
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
  settle?.();
}

export interface WebAudioPlaybackLease {
  isCurrent: () => boolean;
  play: (url: string) => Promise<boolean>;
  cancel: () => void;
}

export function beginWebAudioPlaybackRequest(): WebAudioPlaybackLease {
  const generation = ++playbackGeneration;
  cancelActivePlayback();
  const isCurrent = () => generation === playbackGeneration && !captureOwner;
  return {
    isCurrent,
    play: (url) => isCurrent() ? playClip(url) : Promise.resolve(false),
    cancel: () => {
      if (generation === playbackGeneration) stopWebAudio();
    },
  };
}

export async function playWebAudio(url: string): Promise<boolean> {
  return beginWebAudioPlaybackRequest().play(url);
}

export async function playWebAudioSequence(
  urls: readonly string[]
): Promise<boolean> {
  const generation = ++playbackGeneration;
  if (urls.length === 0 || captureOwner) return false;
  for (const url of urls) {
    if (generation !== playbackGeneration || captureOwner) return false;
    if (!(await playClip(url))) return false;
  }
  return generation === playbackGeneration;
}

async function playClip(url: string): Promise<boolean> {
  if (!url) {
    throw new Error("audio_url_missing");
  }
  if (captureOwner) return false;

  cancelActivePlayback();

  const audio = new Audio(url);
  activeAudio = audio;

  try {
    return await new Promise<boolean>((resolve, reject) => {
      let settled = false;
      let timeout: ReturnType<typeof setTimeout>;
      const cleanup = () => {
        clearTimeout(timeout);
        audio.removeEventListener("ended", onEnded);
        audio.removeEventListener("error", onError);
        audio.removeEventListener("loadedmetadata", onMetadata);
        if (settleActivePlayback === settle) {
          settleActivePlayback = null;
        }
      };
      const settle = (completed = false) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(completed);
      };
      const onEnded = () => settle(true);
      const onError = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error("audio_playback_failed"));
      };
      const onTimeout = () => {
        if (settled) return;
        settled = true;
        cleanup();
        audio.pause();
        audio.currentTime = 0;
        reject(new Error("audio_playback_timeout"));
      };
      const onMetadata = () => {
        if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
        clearTimeout(timeout);
        timeout = setTimeout(onTimeout, Math.max(AUDIO_START_TIMEOUT_MS, audio.duration * 1000 + 10_000));
      };
      settleActivePlayback = settle;
      audio.addEventListener("ended", onEnded, { once: true });
      audio.addEventListener("error", onError, { once: true });
      audio.addEventListener("loadedmetadata", onMetadata, { once: true });
      timeout = setTimeout(onTimeout, AUDIO_START_TIMEOUT_MS);
      onMetadata();
      try {
        void audio.play().catch(onError);
      } catch {
        onError();
      }
    });
  } finally {
    if (activeAudio === audio) {
      activeAudio = null;
      settleActivePlayback = null;
    }
  }
}

export function stopWebAudio(): void {
  playbackGeneration += 1;
  cancelActivePlayback();
}
